import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { THEME_COLOR } from '../../../theme/formStyles';
import { fmtRange } from '../projectSchedule.utils';
import { STEP_STYLE, SUB_STATUS_BAR, columnsFor, durationDays, posPct, spanPct, stepPercent } from './gantt.utils';

const LEFT_W = 300;
const COL_W = { week: 46, month: 72 };
const STAGE_COLOR = { open: '#dc2626', assigned: '#ea580c', scheduled: '#2563eb', resolved: '#16a34a' };
const STAGE_LABEL = { open: 'Open', assigned: 'Engineer assigned', scheduled: 'Visit scheduled', resolved: 'Resolved' };

const fmt = (d) => dayjs(d).format('D MMM YYYY');

function Row({ num, label, duration, indent = 0, bold, muted, expander, onClick, height = 34, children }) {
    return (
        <Box sx={{ display: 'flex', borderBottom: '1px solid #eef1f4', minHeight: height, '&:hover .gl': { bgcolor: '#f9fbfc' } }}>
            <Box className="gl" onClick={onClick} sx={{ position: 'sticky', left: 0, zIndex: 2, width: LEFT_W, flex: 'none', bgcolor: '#fff', display: 'flex', alignItems: 'center',
                borderRight: '1px solid #e5e9ee', cursor: onClick ? 'pointer' : 'default', fontSize: '12.5px' }}>
                <Box sx={{ width: 30, textAlign: 'center', color: '#94a3b8', fontSize: '11.5px', flex: 'none' }}>{num}</Box>
                <Box sx={{ width: 22, flex: 'none', display: 'grid', placeItems: 'center' }}>{expander}</Box>
                <Typography noWrap title={typeof label === 'string' ? label : undefined}
                    sx={{ flex: 1, minWidth: 0, pl: indent, fontSize: indent ? '12px' : '12.5px', fontWeight: bold ? 600 : 400, color: muted ? '#6b7785' : '#1f2933' }}>{label}</Typography>
                <Box sx={{ width: 70, flex: 'none', textAlign: 'right', pr: 1, color: '#6b7785', fontSize: '11.5px' }}>{duration}</Box>
            </Box>
            <Box sx={{ position: 'relative', flex: 1 }}>{children}</Box>
        </Box>
    );
}

function Bar({ span, color, textColor = '#fff', pct, label, title, thin, onClick }) {
    if (!span) return null;
    return (
        <Tooltip title={title || ''} disableInteractive>
            <Box onClick={onClick} sx={{ position: 'absolute', top: thin ? 11 : 7, bottom: thin ? 11 : 7, left: `${span.left}%`, width: `max(${span.width}%, 6px)`,
                bgcolor: pct !== undefined ? `${color}55` : color, borderRadius: span.clipLeft ? '0 5px 5px 0' : span.clipRight ? '5px 0 0 5px' : '5px', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default' }}>
                {pct !== undefined && <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: color }} />}
                {label && <Typography noWrap sx={{ position: 'relative', fontSize: '10.5px', fontWeight: 700, color: textColor, px: 0.75, lineHeight: thin ? '12px' : '20px' }}>{label}</Typography>}
            </Box>
        </Tooltip>
    );
}

function Marker({ date, range, shape = 'diamond', color, title, onClick }) {
    const left = posPct(date, range);
    if (left === null) return null;
    return (
        <Tooltip title={title} disableInteractive>
            <Box onClick={onClick} sx={{ position: 'absolute', top: '50%', left: `${left}%`, width: 12, height: 12, ml: '-6px', mt: '-6px', bgcolor: color, cursor: onClick ? 'pointer' : 'default',
                borderRadius: shape === 'dot' ? '50%' : '2px', transform: shape === 'diamond' ? 'rotate(45deg)' : 'none', border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.08)', zIndex: 1 }} />
        </Tooltip>
    );
}

// Table Gantt: # / step / duration on the left, week (project tab) or month (financial year) columns on the right.
// Task steps expand to their sub-tasks. Extra rows: handover, invoicing, calibration, tickets, contract period.
export default function ProjectGantt({ plan, range, unit = 'week', showContract = false, onOpenSubTask, onOpenTicket }) {
    const cols = useMemo(() => columnsFor(range, unit), [range, unit]);
    const [open, setOpen] = useState(() => new Set());
    const today = dayjs().format('YYYY-MM-DD');
    const todayLeft = posPct(today, range);
    const toggle = (key) => setOpen(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

    const steps = plan.steps || [];
    const tickets = plan.tickets || [];
    const calibrations = plan.calibrations || [];
    const invoices = plan.invoices || [];
    const inRange = (d) => d && d >= range.start && d <= range.end;
    let rowNo = 0;

    return (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 2, overflowX: 'auto', bgcolor: '#fff',
            // Faded right edge hints there is more to scroll
            maskImage: 'linear-gradient(to right, #000 calc(100% - 18px), transparent)', WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 18px), transparent)' }}>
            <Box sx={{ minWidth: LEFT_W + cols.length * COL_W[unit], position: 'relative', pr: '18px' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3, bgcolor: '#fafbfc', borderBottom: '1px solid #e5e9ee' }}>
                    <Box sx={{ position: 'sticky', left: 0, zIndex: 4, width: LEFT_W, flex: 'none', bgcolor: '#fafbfc', borderRight: '1px solid #e5e9ee', display: 'flex', alignItems: 'center', fontSize: '11px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        <Box sx={{ width: 30, textAlign: 'center' }}>#</Box><Box sx={{ width: 22 }} /><Box sx={{ flex: 1 }}>Task</Box><Box sx={{ width: 70, textAlign: 'right', pr: 1 }}>Duration</Box>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex' }}>
                        {cols.map(c => (
                            <Box key={c.start} sx={{ flex: daysBetweenFlex(c), minWidth: 0, textAlign: 'center', py: 0.75, fontSize: '11px', color: '#6b7785', borderLeft: '1px solid #eef1f4', bgcolor: c.band ? '#f4f6f8' : 'transparent', lineHeight: 1.25 }}>
                                {c.label}{c.sub && <><br /><span style={{ fontSize: '10px', color: '#94a3b8' }}>{c.sub}</span></>}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Body: column bands and today line sit behind the rows */}
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: LEFT_W, right: '18px', display: 'flex', pointerEvents: 'none' }}>
                        {cols.map(c => <Box key={c.start} sx={{ flex: daysBetweenFlex(c), borderLeft: '1px solid #eef1f4', bgcolor: c.band ? '#fafbfc' : 'transparent' }} />)}
                        {todayLeft !== null && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${todayLeft}%`, width: 2, bgcolor: '#ef4444', zIndex: 1 }} />}
                    </Box>

                    {steps.map((s, i) => {
                        const key = s.id ? `s${s.id}` : `t${s.task_id}-${i}`;
                        const style = STEP_STYLE[s.status] || STEP_STYLE.upcoming;
                        const pct = stepPercent(s);
                        const days = durationDays(s.start_date, s.end_date);
                        const isOpen = open.has(key);
                        const subs = s.sub_tasks || [];
                        rowNo += 1;
                        return (
                            <React.Fragment key={key}>
                                <Row num={rowNo} label={s.label} bold={!!subs.length} duration={days ? `${days} d` : (s.date_label || '')}
                                    onClick={subs.length ? () => toggle(key) : undefined}
                                    expander={subs.length ? <IconButton size="small" sx={{ p: 0.25 }}><ChevronRightIcon sx={{ fontSize: 18, transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }} /></IconButton> : null}>
                                    {s.start_date
                                        ? <Bar span={spanPct(s.start_date, s.end_date, range)} color={style.bar} textColor={pct > 35 ? '#fff' : '#334155'} pct={pct} label={`${pct}%`}
                                            title={`${s.label} · ${fmtRange(s.start_date, s.end_date, 'D MMM YYYY')} · ${style.label}${s.total_sub_tasks ? ` · ${s.completed_sub_tasks}/${s.total_sub_tasks} sub-tasks` : ''}`} />
                                        : s.date_label && <Typography sx={{ position: 'absolute', left: 8, top: 8, fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>{s.date_label}</Typography>}
                                </Row>
                                {isOpen && subs.map(st => (
                                    <Row key={`st${st.id}`} indent={1.5} muted label={st.name} height={28} duration={durationDays(st.start_date, st.end_date) ? `${durationDays(st.start_date, st.end_date)} d` : ''}
                                        onClick={onOpenSubTask ? () => onOpenSubTask(st) : undefined}>
                                        <Bar thin span={spanPct(st.start_date, st.end_date, range)} color={SUB_STATUS_BAR[st.status] || SUB_STATUS_BAR.Pending}
                                            title={`${st.name} · ${fmtRange(st.start_date, st.end_date, 'D MMM YYYY')} · ${st.status}${st.spoc?.name ? ` · ${st.spoc.name}` : ''}`}
                                            onClick={onOpenSubTask ? () => onOpenSubTask(st) : undefined} />
                                    </Row>
                                ))}
                            </React.Fragment>
                        );
                    })}

                    {plan.handover_date && inRange(plan.handover_date) && (
                        <Row num="◆" label="Handover to client" bold duration={dayjs(plan.handover_date).format('D MMM')}>
                            <Marker date={plan.handover_date} range={range} color="#7c3aed" title={`Handover · ${fmt(plan.handover_date)}`} />
                        </Row>
                    )}
                    {invoices.some(inRange) && (
                        <Row num="₹" label="Invoicing" duration={`${invoices.filter(inRange).length} due`}>
                            {invoices.map((d, i) => <Marker key={d} date={d} range={range} color="#ca8a04" title={`${i === 0 ? 'First invoice (handover + 10 days)' : `Year ${i + 1} invoice`} · ${fmt(d)}`} />)}
                        </Row>
                    )}
                    {calibrations.some(c => inRange(c.date)) && (
                        <Row num="⚙" label="Calibration" duration={`${calibrations.filter(c => inRange(c.date)).length} visits`}>
                            {calibrations.map(c => <Marker key={c.id} shape="dot" date={c.date} range={range} color={c.status === 'Completed' ? '#16a34a' : '#2563eb'} title={`Calibration · ${fmt(c.date)} · ${c.status}${c.engineer?.name ? ` · ${c.engineer.name}` : ''}`} />)}
                        </Row>
                    )}
                    {tickets.some(t => inRange(dayjs(t.created_at).format('YYYY-MM-DD')) || inRange(t.scheduled_date)) && (
                        <Row num="🎫" label="Tickets" duration={`${tickets.filter(t => t.stage !== 'resolved').length} open`}>
                            {tickets.map(t => {
                                const date = t.stage === 'scheduled' && t.scheduled_date ? t.scheduled_date : dayjs(t.created_at).format('YYYY-MM-DD');
                                return <Marker key={t.id} shape="dot" date={date} range={range} color={STAGE_COLOR[t.stage]}
                                    title={`${t.id} · ${t.title} · ${STAGE_LABEL[t.stage]}${t.scheduled_date ? ` · visit ${fmt(t.scheduled_date)}` : ''}`}
                                    onClick={onOpenTicket ? () => onOpenTicket(t) : undefined} />;
                            })}
                        </Row>
                    )}
                    {showContract && plan.contract && spanPct(plan.contract.start, plan.contract.end, range) && (
                        <Row num="📄" label={`Contract · ${plan.contract.years} year${plan.contract.years > 1 ? 's' : ''}`} duration={dayjs(plan.contract.end).format('MMM YYYY')}>
                            <Bar span={spanPct(plan.contract.start, plan.contract.end, range)} color="#99d5cf" textColor="#0f4d4a" label="Contract period"
                                title={`Contract · ${fmt(plan.contract.start)} – ${fmt(plan.contract.end)}`} />
                        </Row>
                    )}
                    {!steps.length && <Typography sx={{ p: 2, color: '#6b7785', fontSize: '13px' }}>No steps on this project yet.</Typography>}
                </Box>
            </Box>
        </Box>
    );
}

// Columns are sized by their number of days so bars line up with the header
function daysBetweenFlex(c) {
    return `${dayjs(c.end).diff(dayjs(c.start), 'day') + 1} 1 0`;
}

export function GanttLegend({ showFinance = true }) {
    const items = [
        ...Object.values(STEP_STYLE).map(s => ({ c: s.bar, l: s.label })),
        { c: '#7c3aed', l: 'Handover', d: true },
        ...(showFinance ? [{ c: '#ca8a04', l: 'Invoice', d: true }] : []),
        { c: '#2563eb', l: 'Calibration', dot: true },
        { c: '#dc2626', l: 'Open ticket', dot: true },
        { c: '#ef4444', l: 'Today', line: true },
    ];
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', mt: 1.25, fontSize: '11.5px', color: '#6b7785' }}>
            {items.map(i => (
                <Box key={i.l} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: i.line ? 2 : 10, height: i.line ? 12 : 10, bgcolor: i.c, borderRadius: i.dot ? '50%' : '2px', transform: i.d ? 'rotate(45deg) scale(.85)' : 'none' }} />{i.l}
                </Box>
            ))}
            <Box sx={{ color: THEME_COLOR }}>Click a task to show its sub-tasks</Box>
        </Box>
    );
}
